import type {SniffedImage, SniffedImageFormat, WebpFlavor} from '../types';

const MIME_TYPE_BY_FORMAT: Readonly<Record<SniffedImageFormat, string>> = {
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  bmp: 'image/bmp',
  heic: 'image/heic',
  avif: 'image/avif',
};

const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'mif1',
  'msf1',
]);
const AVIF_BRANDS = new Set(['avif', 'avis']);
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;
const PNG_ALPHA_COLOR_TYPES = new Set([4, 6]);
const WEBP_VP8X_ALPHA_FLAG = 0x10;
const WEBP_VP8X_EXIF_FLAG = 0x08;

type Sniffer = (bytes: Uint8Array) => SniffedImage | null;

const startsWith = (bytes: Uint8Array, offset: number, expected: readonly number[]): boolean =>
  expected.every((value, index) => bytes[offset + index] === value);

const asciiAt = (bytes: Uint8Array, offset: number, length: number): string | null => {
  if (offset + length > bytes.length) {
    return null;
  }
  let text = '';
  for (let index = offset; index < offset + length; index += 1) {
    text += String.fromCharCode(bytes[index] ?? 0);
  }
  return text;
};

const readUint32Le = (bytes: Uint8Array, offset: number): number | null => {
  const b0 = bytes[offset];
  const b1 = bytes[offset + 1];
  const b2 = bytes[offset + 2];
  const b3 = bytes[offset + 3];
  if (b0 === undefined || b1 === undefined || b2 === undefined || b3 === undefined) {
    return null;
  }
  return (b0 | (b1 << 8) | (b2 << 16) | (b3 << 24)) >>> 0;
};

const readUint32Be = (bytes: Uint8Array, offset: number): number | null => {
  const b0 = bytes[offset];
  const b1 = bytes[offset + 1];
  const b2 = bytes[offset + 2];
  const b3 = bytes[offset + 3];
  if (b0 === undefined || b1 === undefined || b2 === undefined || b3 === undefined) {
    return null;
  }
  return ((b0 << 24) | (b1 << 16) | (b2 << 8) | b3) >>> 0;
};

const sniffed = (
  format: SniffedImageFormat,
  details: Partial<Pick<SniffedImage, 'webpFlavor' | 'hasAlpha' | 'hasExifFlag'>> = {},
): SniffedImage => ({
  format,
  mimeType: MIME_TYPE_BY_FORMAT[format],
  webpFlavor: details.webpFlavor ?? null,
  hasAlpha: details.hasAlpha ?? false,
  hasExifFlag: details.hasExifFlag ?? false,
});

const sniffJpeg: Sniffer = bytes =>
  startsWith(bytes, 0, [0xff, 0xd8, 0xff]) ? sniffed('jpeg') : null;

const sniffPng: Sniffer = bytes => {
  if (!startsWith(bytes, 0, PNG_SIGNATURE)) {
    return null;
  }
  const colorType = bytes[25];
  return sniffed('png', {
    hasAlpha: colorType !== undefined && PNG_ALPHA_COLOR_TYPES.has(colorType),
  });
};

const WEBP_FLAVOR_BY_CHUNK: Readonly<Record<string, WebpFlavor>> = {
  'VP8 ': 'lossy',
  VP8L: 'lossless',
  ANIM: 'lossy',
  ANMF: 'lossy',
};

/** VP8X is a container: the bitstream chunk that follows says whether the image is lossless. */
const findExtendedWebpFlavor = (bytes: Uint8Array): WebpFlavor | null => {
  let offset = 30;
  while (offset + 8 <= bytes.length) {
    const fourcc = asciiAt(bytes, offset, 4);
    const size = readUint32Le(bytes, offset + 4);
    if (fourcc === null || size === null) {
      return null;
    }
    const flavor = WEBP_FLAVOR_BY_CHUNK[fourcc];
    if (flavor !== undefined) {
      return flavor;
    }
    offset += 8 + size + (size % 2);
  }
  return null;
};

const sniffWebp: Sniffer = bytes => {
  if (asciiAt(bytes, 0, 4) !== 'RIFF' || asciiAt(bytes, 8, 4) !== 'WEBP') {
    return null;
  }
  const chunk = asciiAt(bytes, 12, 4);
  if (chunk === 'VP8 ') {
    return sniffed('webp', {webpFlavor: 'lossy'});
  }
  if (chunk === 'VP8L') {
    const alphaByte = bytes[24] ?? 0;
    return sniffed('webp', {webpFlavor: 'lossless', hasAlpha: ((alphaByte >> 4) & 1) === 1});
  }
  if (chunk === 'VP8X') {
    const flags = bytes[20] ?? 0;
    return sniffed('webp', {
      webpFlavor: findExtendedWebpFlavor(bytes),
      hasAlpha: (flags & WEBP_VP8X_ALPHA_FLAG) !== 0,
      hasExifFlag: (flags & WEBP_VP8X_EXIF_FLAG) !== 0,
    });
  }
  return sniffed('webp');
};

const sniffGif: Sniffer = bytes => {
  const header = asciiAt(bytes, 0, 6);
  return header === 'GIF87a' || header === 'GIF89a' ? sniffed('gif', {hasAlpha: true}) : null;
};

const sniffBmp: Sniffer = bytes => (asciiAt(bytes, 0, 2) === 'BM' ? sniffed('bmp') : null);

const collectFtypBrands = (bytes: Uint8Array): readonly string[] => {
  const boxSize = readUint32Be(bytes, 0) ?? 0;
  const end = Math.min(boxSize, bytes.length);
  const brands: string[] = [];
  const major = asciiAt(bytes, 8, 4);
  if (major !== null) {
    brands.push(major);
  }
  for (let offset = 16; offset + 4 <= end; offset += 4) {
    const brand = asciiAt(bytes, offset, 4);
    if (brand !== null) {
      brands.push(brand);
    }
  }
  return brands;
};

const sniffIsoBmff: Sniffer = bytes => {
  if (asciiAt(bytes, 4, 4) !== 'ftyp') {
    return null;
  }
  const brands = collectFtypBrands(bytes);
  if (brands.some(brand => AVIF_BRANDS.has(brand))) {
    return sniffed('avif', {hasAlpha: true});
  }
  if (brands.some(brand => HEIC_BRANDS.has(brand))) {
    return sniffed('heic');
  }
  return null;
};

const SNIFFERS: readonly Sniffer[] = [
  sniffJpeg,
  sniffPng,
  sniffWebp,
  sniffGif,
  sniffBmp,
  sniffIsoBmff,
];

/** Identifies an image from its leading bytes; returns `null` for anything that is not a known raster format. */
export const sniffImageFormat = (bytes: Uint8Array): SniffedImage | null => {
  for (const sniffer of SNIFFERS) {
    const result = sniffer(bytes);
    if (result !== null) {
      return result;
    }
  }
  return null;
};
